<template>
  <net-canvas
    ref="canvasElement"
    mode="view"
    :relations="relations"
    :targets="targets"
    :resources="resources"
    :indices="indices"
    :flows="flows"
    :flow-branches="flowBranches"
    :element-values="elementValues"
    :selection="selectionStore.selection"
    :net-transform="sceneTree.netTransform"
    @select-item="select"
    @add-select-item="addToSelection"
    @select-many="selectMany"
    @clear-all-selection="deselectAll"
    @delete="noop"
    @contextmenu="onContextmenu($event)"
    @click="onClick"
    @update:net-transform="sceneTree.updateNetTransform"
    @update:canvas-dimensions="updateCanvasDimensions"
  >
    <net-sidebar-toggle-button />
  </net-canvas>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import NetCanvas from "@editor/net/NetCanvas.vue";
import NetSidebarToggleButton from "@editor/net/NetSidebarToggleButton.vue";

import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useRightClickMenu } from "@core/ui/rightClickMenu.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useOverlay } from "@editor/ui/overlayDialog.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useFlowStore } from "@editor/items/flow/flows.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useNetStore } from "@editor/net/net.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useElements } from "@editor/items/element/elements";
import { useVariablesViewStore } from "./variablesView.store";

import type { CanvasDimensions } from "@editor/net/CanvasDimensions";
import type { ContextItemType, NetSelectableItemType } from "@editor/net/NetCanvasProps";
import { splitUid, toUid, type Uid } from "@/helpers/utility/Id";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import { isSceneElementNode } from "@editor/items/sceneElement/sceneElementFunctions";
import { isInList } from "@/helpers/utility/Type";
import { watch } from "vue";
import { useSceneStore } from "@editor/items/scene/scenes.store";
import { usePresenceStore } from "@editor/store/presence.store";
import { useSceneNodesStore } from "@editor/items/scene/sceneNodes.store";

const emit = defineEmits<{
  (event: "click", mouseEvent: MouseEvent): void;
  (event: "deactivate"): void;
}>();

const noop = function () {};
const canvasElement = ref<InstanceType<typeof NetCanvas>>();

const { t } = useI18n();
const selectionStore = useSelectionStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();
const indexStore = useIndexStore();
const variablesView = useVariablesViewStore();
const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();

const relations = computed(() => useRelationStore().items);
const targets = computed(() => targetStore.items);
const resources = computed(() => resourceStore.items);
const indices = computed(() => indexStore.items);
const flows = computed(() => useFlowStore().items);
const flowBranches = computed(() => useFlowBranchStore().items);

const elementValues = computed(() => useElements().elementValues);

watch(() => sceneTree.activeScene, (scene) => {
  if (scene == null) return;
  if (scene.editModeOnly) {
    console.log("active scene is editmode only, switching to default");
    const nonEditScene = useSceneStore().orderedItems.find(s => !s.editModeOnly);
    if (!nonEditScene) {
      console.warn("no non-edit scene found, staying on editmode only scene");
      return;
    }
    usePresenceStore().set("activeSceneId", nonEditScene.id);
  }
}, { immediate: true });

function select(item: { uid: Uid<NetSelectableItemType> }) {
  if (selectionStore.contains(item)) {
    const { type, id } = splitUid(item.uid);
    openItemInfo({ type, id });
  } else {
    selectionStore.select(item);
  }
}
function addToSelection(item: { uid: Uid<NetSelectableItemType> }) {
  selectionStore.add(item);
}
function selectMany(items: { uid: Uid<NetSelectableItemType> }[]) {
  selectionStore.selectMany(items);
}
function deselectAll() {
  selectionStore.clearAll();
}
function onClick(mouseEvent: MouseEvent) {
  variablesView.selectMain(null);
  emit("click", mouseEvent);
}
function openItemInfo(item: { type: NetSelectableItemType; id: number }) {
  if (!item) return;
  if (item.type === "sceneNode") {
    const node = sceneNodeStore.byUid[toUid("sceneNode", item.id)];
    if (node && isSceneElementNode(node)) {
      const { type: elementType, id: elementId } = splitUid(node.data.elementUid);
      useOverlay().setItem({ type: elementType, id: elementId });
      return;
    }
    return;
  }
  useOverlay().setItem({ type: item.type, id: item.id });
}

function onContextmenu(
  event: MouseEvent & {
    items?: { uid: Uid<ContextItemType> }[] | null;
    item?: { uid: Uid<ContextItemType> } | null;
  }
) {
  let actions = [];

  let item = event.item;
  if (!item && event.items && event.items.length > 0) {
    item = event.items[0];
  }

  if (item) {
    const { type: itemType, id: itemId } = splitUid(item.uid);
    if (
      isInList(itemType, [
        "target",
        "resource",
        "index",
        "sceneNode",
        "relation",
        "momentum",
        "flow",
        "flowBranch"
      ])
    ) {
      actions.unshift({
        title: t("actions.showInformations"),
        callback: () => {
          openItemInfo({ type: itemType, id: itemId });
        }
      });
    }
  } else {
    actions.push({
      title: t("actions.adjustView"),
      callback: () => {
        const net = canvasElement.value;
        if (net) {
          net.adjustView();
        }
      }
    });
  }

  useRightClickMenu().show({
    x: event.clientX,
    y: event.clientY,
    actions: actions
  });
}

function updateCanvasDimensions(dimensions: CanvasDimensions) {
  useNetStore().canvasDimensions = dimensions;
}
</script>

<style></style>
